import React, { useState, useEffect } from 'react';
import { trackUserEvent } from '@/utils/userTracking';
import { dataService, PESnapshotData, CommentaryType } from '@rti/shared';
import { useTheme } from '@/contexts/ThemeContext';
import {
  normalizeRows,
  getChange,
  getSortedDates,
  getAvailableDates,
  getFirstDateWithPrelimCommentary,
  formatDateLabel,
  formatCommentary,
  renderStatCard,
  hasCommentaryData
} from './snapshotUtils';
import { createChart, rsColors, formatLabel, commonChartOptions, getAxisColors } from './chartUtils';
import { Chart } from 'chart.js';
import { normalizeSnapshotResponse } from './snapshotDataUtils';

interface SnapshotPEProps {
  data?: PESnapshotData[];
  commentaryType?: 'preliminary' | 'final';
  onCommentaryTypeChange?: (type: 'preliminary' | 'final') => void;
  onSelectedDateChange?: (date: string) => void;
}

const SnapshotPE: React.FC<SnapshotPEProps> = ({ onCommentaryTypeChange, onSelectedDateChange }) => {
  const { isDark } = useTheme();
  const { tickColor, gridColor } = getAxisColors(isDark);
  const [stats, setStats] = useState<any>({});
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [commentaryTexts, setCommentaryTexts] = useState<any>({});
  const [commentaryType, setCommentaryType] = useState<CommentaryType>('preliminary');
  const [chartInstances, setChartInstances] = useState<{ [key: string]: Chart | null }>({});
  const [prelimData, setPrelimData] = useState<PESnapshotData[]>([]);
  const [finalData, setFinalData] = useState<PESnapshotData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const currentData: PESnapshotData[] = commentaryType === 'final' && finalData.length > 0 ? finalData : prelimData;

  const handleCommentaryTypeChange = (type: CommentaryType) => {
    setCommentaryType(type);
    onCommentaryTypeChange?.(type);
  };

  useEffect(() => {
    const loadBothDatasets = async () => {
      try {
        setLoading(true);
        const [prelimResp, finalResp] = await Promise.all([
          dataService.getACCPEData(),
          dataService.getACCPEDataFinal()
        ]);
        const prelimArray = normalizeSnapshotResponse<PESnapshotData>(prelimResp) || [];
        const finalArray = normalizeSnapshotResponse<PESnapshotData>(finalResp) || [];
        setPrelimData(prelimArray);
        setFinalData(finalArray);

        const base = finalArray.length > 0 ? finalArray : prelimArray;
        if (base.length > 0) {
          initializeFromBase(base, finalArray, prelimArray);
        }
        trackUserEvent('monthly_pe_snapshot_view', 'Monthly PE Snapshot');
      } finally {
        setLoading(false);
      }
    };

    loadBothDatasets();
  }, []);

  const initializeFromBase = (rows: PESnapshotData[], finalDataArray: PESnapshotData[], prelimDataArray: PESnapshotData[]) => {
    const validRows = rows.filter(row => row.date && row.date.trim() !== '');
    if (validRows.length === 0) return;
    const normalizedRows = normalizeRows(validRows);
    const sorted = getSortedDates(normalizedRows);
    const prelimDates = (prelimDataArray || []).map(d => d.date).filter(Boolean);

    const baseAvailable = getAvailableDates(sorted, 25, prelimDataArray);
    const available = baseAvailable.filter(date => prelimDates.includes(date));

    setAvailableDates(available);

    const firstDateWithCommentary = getFirstDateWithPrelimCommentary(prelimDataArray, 25);
    const defaultDate = firstDateWithCommentary || available[0] || sorted[0].date;

    const defaultRow = finalDataArray.find(f => f.date === defaultDate);
    const shouldUseFinal = defaultRow && hasCommentaryData(defaultRow);

    setCommentaryType(shouldUseFinal ? 'final' : 'preliminary');
    onCommentaryTypeChange?.(shouldUseFinal ? 'final' : 'preliminary');

    setSelectedDate(defaultDate);
    onSelectedDateChange?.(defaultDate);
    updateDataForSelectedDate(defaultDate, normalizedRows);
  };

  useEffect(() => {
    if (!selectedDate || currentData.length === 0) return;
    const validRows = currentData.filter(row => row.date && row.date.trim() !== '');
    if (validRows.length === 0) return;
    const normalizedRows = normalizeRows(validRows);
    updateDataForSelectedDate(selectedDate, normalizedRows);
  }, [selectedDate, commentaryType, prelimData, finalData, isDark]);

  const renderCharts = (cumulativeData: PESnapshotData[], othersData: PESnapshotData[]) => {

    Object.values(chartInstances).forEach(chart => {
      if (chart) {
        try {
          chart.destroy();
        } catch (error) {
          console.warn('Error destroying chart:', error);
        }
      }
    });


    setChartInstances({});


    setTimeout(() => {
      createNewCharts(cumulativeData, othersData);
    }, 50);
  };

  const createNewCharts = (cumulativeData: PESnapshotData[], othersData: PESnapshotData[]) => {
    const validCumulativeData = (cumulativeData || []).filter(d => d.date && d.date.trim() !== '');
    if (validCumulativeData.length === 0) {
      console.warn('No valid cumulative data with proper dates found');
      return;
    }
    const cumulativeSorted = [...validCumulativeData].sort((a, b) => new Date(a.date) > new Date(b.date) ? 1 : -1);
    const cumulativeLabels = cumulativeSorted.map(d => formatLabel(d.date));

    const validOthersData = (othersData || []).filter(d => d.date && d.date.trim() !== '');
    if (validOthersData.length === 0) {
      console.warn('No valid non-cumulative data with proper dates found');
      return;
    }
    const othersSorted = [...validOthersData].sort((a, b) => new Date(a.date) > new Date(b.date) ? 1 : -1);
    const othersLabels = othersSorted.map(d => formatLabel(d.date));

    const lineColor = rsColors.rbEnd;
    const capLineCfg = { type: 'line' as const, borderColor: lineColor, fill: false, order: 3, yAxisID: 'y', pointRadius: 0 };

    try {
      const peInventory = cumulativeSorted.map(d => +(d.chart_pe_inventory || 0));
      const peMonthlyDiff = peInventory.map((v, i, arr) => (i === 0 ? 0 : v - arr[i - 1]));
      const peCumulative = peMonthlyDiff.reduce((acc, v) => { acc.push(acc.at(-1)! + v); return acc; }, [0]).slice(1);

      const cumulativeCanvas = document.getElementById('peCumulativeChart') as HTMLCanvasElement;
      if (!cumulativeCanvas) {
        console.error('Canvas element peCumulativeChart not found');
        return;
      }

      const cumulativeChart = createChart(cumulativeCanvas, {
        type: 'line',
        data: { labels: cumulativeLabels, datasets: [{ label: 'PE Cumulative Draws/Gains', data: peCumulative, borderColor: '#854cff', backgroundColor: 'rgba(133,76,255,.1)', tension: 0.2, pointRadius: 0 }] },
        options: { ...commonChartOptions, plugins: { legend: { display: false }, datalabels: { display: false } }, scales: { x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } }, y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: (c: any) => (c.tick.value === 0 ? 2 : 0.3) } } } }
      });

      const prodSalesCanvas = document.getElementById('peProdSalesChart') as HTMLCanvasElement;
      if (!prodSalesCanvas) {
        console.error('Canvas element peProdSalesChart not found');
        return;
      }

      const prodSalesChart = createChart(prodSalesCanvas, {
        type: 'line',
        data: { labels: othersLabels, datasets: [
          { label: 'Domestic Production', data: othersSorted.map(d => +(d.chart_pe_domestic_production || 0)), borderColor: rsColors.darkGreen, fill: false, order: 3, yAxisID: 'y', pointRadius: 0 },
          { label: 'Total Sales', data: othersSorted.map(d => +(d.chart_pe_total_sales || 0)), borderColor: rsColors.purple, fill: false, order: 2, yAxisID: 'y', pointRadius: 0 }
        ] },
        options: { ...commonChartOptions, plugins: { legend: { display: true, labels: { color: tickColor } }, datalabels: { display: false } }, scales: { x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } }, y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } } } }
      });

      const createProdSalesChart = (canvasId: string, prefix: string, colorProd: string, colorSales: string) => {
        const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
        if (!canvas) { console.error(`Canvas element ${canvasId} not found`); return null; }
        const cap = othersSorted.map(d => +(d[`chart_${prefix}_capacity` as keyof PESnapshotData] || 0));
        const prod = othersSorted.map(d => +(d[`chart_${prefix}_production` as keyof PESnapshotData] || 0));
        const sales = othersSorted.map(d => +(d[`chart_${prefix}_sales` as keyof PESnapshotData] || 0));
        return createChart(canvas, {
          type: 'line',
          data: { labels: othersLabels, datasets: [
            { ...capLineCfg, label: 'Capacity', data: cap },
            { type: 'line' as const, label: 'Production', data: prod, borderColor: colorProd, order: 1, yAxisID: 'y', pointRadius: 0 },
            { type: 'line' as const, label: 'Sales', data: sales, borderColor: colorSales, order: 1, yAxisID: 'y', pointRadius: 0 }
          ] },
          options: { ...commonChartOptions, plugins: { legend: { position: 'top' as const, labels: { color: tickColor } }, datalabels: { display: false } }, scales: { x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } }, y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } } } }
        });
      };

      const ldpeChart = createProdSalesChart('ldpeProdSalesChart', 'ldpe', rsColors.darkGreen, rsColors.purple);
      const hdpeChart = createProdSalesChart('hdpeProdSalesChart', 'hdpe', rsColors.darkGreen, rsColors.purple);
      const lldpeChart = createProdSalesChart('lldpeProdSalesChart', 'lldpe', rsColors.darkGreen, rsColors.purple);

      setChartInstances({ cumulative: cumulativeChart, prodSales: prodSalesChart, ldpe: ldpeChart, hdpe: hdpeChart, lldpe: lldpeChart });
    } catch (error) {
      console.error('Error creating PE charts:', error);
    }
  };

  const updateDataForSelectedDate = (selectedDate: string, rows: PESnapshotData[] = currentData) => {
    const sortedRows = [...rows].sort((a, b) => new Date(b.date) > new Date(a.date) ? 1 : -1);
    const currentRow = sortedRows.find(r => r.date === selectedDate);
    const currentIndex = sortedRows.findIndex(r => r.date === selectedDate);
    const prevRow = sortedRows[currentIndex + 1];
    if (!currentRow) return;

    const peStats = {
      peDays: {
        value: Math.round(currentRow.pe_days || 0),
        change: getChange(currentRow.pe_days || 0, prevRow?.pe_days || 0)
      },
      peDemandRate: {
        value: Math.round((currentRow.pe_demand || 0) * 100),
        change: getChange((currentRow.pe_demand || 0) * 100, (prevRow?.pe_demand || 0) * 100, true)
      },
      peExportRate: {
        value: Math.round((currentRow.pe_export || 0) * 100),
        change: getChange((currentRow.pe_export || 0) * 100, (prevRow?.pe_export || 0) * 100, true)
      },
      peOperatingRate: {
        value: Math.round((currentRow.pe_operating || 0) * 100),
        change: getChange((currentRow.pe_operating || 0) * 100, (prevRow?.pe_operating || 0) * 100, true)
      },
      lldpeDays: {
        value: Math.round(currentRow.lldpe_days || 0),
        change: getChange(currentRow.lldpe_days || 0, prevRow?.lldpe_days || 0)
      },
      lldpeDemandRate: {
        value: Math.round((currentRow.lldpe_demand || 0) * 100),
        change: getChange((currentRow.lldpe_demand || 0) * 100, (prevRow?.lldpe_demand || 0) * 100, true)
      },
      lldpeExportRate: {
        value: Math.round((currentRow.lldpe_export || 0) * 100),
        change: getChange((currentRow.lldpe_export || 0) * 100, (prevRow?.lldpe_export || 0) * 100, true)
      },
      lldpeOperatingRate: {
        value: Math.round((currentRow.lldpe_operating || 0) * 100),
        change: getChange((currentRow.lldpe_operating || 0) * 100, (prevRow?.lldpe_operating || 0) * 100, true)
      },
      hdpeDays: {
        value: Math.round(currentRow.hdpe_days || 0),
        change: getChange(currentRow.hdpe_days || 0, prevRow?.hdpe_days || 0)
      },
      hdpeDemandRate: {
        value: Math.round((currentRow.hdpe_demand || 0) * 100),
        change: getChange((currentRow.hdpe_demand || 0) * 100, (prevRow?.hdpe_demand || 0) * 100, true)
      },
      hdpeExportRate: {
        value: Math.round((currentRow.hdpe_export || 0) * 100),
        change: getChange((currentRow.hdpe_export || 0) * 100, (prevRow?.hdpe_export || 0) * 100, true)
      },
      hdpeOperatingRate: {
        value: Math.round((currentRow.hdpe_operating || 0) * 100),
        change: getChange((currentRow.hdpe_operating || 0) * 100, (prevRow?.hdpe_operating || 0) * 100, true)
      },
      ldpeDays: {
        value: Math.round(currentRow.ldpe_days || 0),
        change: getChange(currentRow.ldpe_days || 0, prevRow?.ldpe_days || 0)
      },
      ldpeDemandRate: {
        value: Math.round((currentRow.ldpe_demand || 0) * 100),
        change: getChange((currentRow.ldpe_demand || 0) * 100, (prevRow?.ldpe_demand || 0) * 100, true)
      },
      ldpeExportRate: {
        value: Math.round((currentRow.ldpe_export || 0) * 100),
        change: getChange((currentRow.ldpe_export || 0) * 100, (prevRow?.ldpe_export || 0) * 100, true)
      },
      ldpeOperatingRate: {
        value: Math.round((currentRow.ldpe_operating || 0) * 100),
        change: getChange((currentRow.ldpe_operating || 0) * 100, (prevRow?.ldpe_operating || 0) * 100, true)
      }
    };

    setStats(peStats);
    setCommentaryTexts({
      cumulativeDraws: currentRow.pe_inv_cum_gains_draws || '',
      productionSales: currentRow.pe_prod_sales || '',
      hdpe: currentRow.hdpe || '',
      ldpe: currentRow.ldpe || '',
      lldpe: currentRow.lldpe || ''
    });

    if (sortedRows.length > 0) {
      const selectedDateObj = new Date(selectedDate);
      const filterByMonths = (limit: number) =>
        sortedRows.filter(d => {
          const currentDate = new Date(d.date);
          const monthsDiff =
            (selectedDateObj.getFullYear() - currentDate.getFullYear()) * 12 +
            (selectedDateObj.getMonth() - currentDate.getMonth());
          return monthsDiff <= limit && monthsDiff >= 0;
        });

      const filteredCumulative = filterByMonths(13);
      const filteredOthers = filterByMonths(12);
      renderCharts(filteredCumulative, filteredOthers);
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    onSelectedDateChange?.(newDate);

    const finalRowForNewDate = finalData.find(f => f.date === newDate);
    const hasFinalCommentaryForNewDate = finalRowForNewDate && hasCommentaryData(finalRowForNewDate);

    if (commentaryType === 'final' && !hasFinalCommentaryForNewDate) {
      setCommentaryType('preliminary');
      onCommentaryTypeChange?.('preliminary');
    }

    trackUserEvent('monthly_pe_snapshot_date_change', 'PE Snapshot Date Changed', {
      date: newDate
    });
  };

  const selectedDateHasFinalCommentary = () => {
    const finalRow = finalData.find(f => f.date === selectedDate);
    return finalRow && hasCommentaryData(finalRow);
  };

  if (loading) {
    return (
      <div className="text-3xl flex text-rs-off-white items-center justify-center h-[500px] w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-4">
          <select
            value={selectedDate}
            onChange={handleDateChange}
            className="border-rs-jet border-2 p-4 rounded-lg"
          >
            {availableDates.filter(date => date && date.trim() !== '').map(date => (
              <option key={date} value={date}>
                {formatDateLabel(date)}
              </option>
            ))}
          </select>
          <select
            value={commentaryType}
            onChange={(e) => handleCommentaryTypeChange(e.target.value as CommentaryType)}
            className="border-rs-jet border-2 p-4 rounded-lg"
          >
            <option value="preliminary">Preliminary</option>
            {selectedDateHasFinalCommentary() && <option value="final">Final</option>}
          </select>
        </div>
      </div>

      {}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {}
        {renderStatCard('PE: Days of Inventory', stats.peDays?.value || 0, stats.peDays?.change || '0', ' days')}
        {renderStatCard('PE: Demand Rate', stats.peDemandRate?.value || 0, stats.peDemandRate?.change || '0', '%')}
        {renderStatCard('PE: Export Rate', stats.peExportRate?.value || 0, stats.peExportRate?.change || '0', '%')}
        {renderStatCard('PE: Operating Rate', stats.peOperatingRate?.value || 0, stats.peOperatingRate?.change || '0', '%')}

        {}
        {renderStatCard('LLDPE: Days of Inventory', stats.lldpeDays?.value || 0, stats.lldpeDays?.change || '0', ' days')}
        {renderStatCard('LLDPE: Demand Rate', stats.lldpeDemandRate?.value || 0, stats.lldpeDemandRate?.change || '0', '%')}
        {renderStatCard('LLDPE: Export Rate', stats.lldpeExportRate?.value || 0, stats.lldpeExportRate?.change || '0', '%')}
        {renderStatCard('LLDPE: Operating Rate', stats.lldpeOperatingRate?.value || 0, stats.lldpeOperatingRate?.change || '0', '%')}

        {}
        {renderStatCard('HDPE: Days of Inventory', stats.hdpeDays?.value || 0, stats.hdpeDays?.change || '0', ' days')}
        {renderStatCard('HDPE: Demand Rate', stats.hdpeDemandRate?.value || 0, stats.hdpeDemandRate?.change || '0', '%')}
        {renderStatCard('HDPE: Export Rate', stats.hdpeExportRate?.value || 0, stats.hdpeExportRate?.change || '0', '%')}
        {renderStatCard('HDPE: Operating Rate', stats.hdpeOperatingRate?.value || 0, stats.hdpeOperatingRate?.change || '0', '%')}

        {}
        {renderStatCard('LDPE: Days of Inventory', stats.ldpeDays?.value || 0, stats.ldpeDays?.change || '0', ' days')}
        {renderStatCard('LDPE: Demand Rate', stats.ldpeDemandRate?.value || 0, stats.ldpeDemandRate?.change || '0', '%')}
        {renderStatCard('LDPE: Export Rate', stats.ldpeExportRate?.value || 0, stats.ldpeExportRate?.change || '0', '%')}
        {renderStatCard('LDPE: Operating Rate', stats.ldpeOperatingRate?.value || 0, stats.ldpeOperatingRate?.change || '0', '%')}
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">PE Cumulative Draws/Gains&nbsp;(MM&nbsp;lbs)</h4>
          <div className="h-[380px]">
            <canvas id="peCumulativeChart" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.cumulativeDraws) }}
            />
          </div>
        </div>

        {}
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">PE Production &amp; Sales&nbsp;(MM&nbsp;lbs)</h4>
          <div className="h-[380px]">
            <canvas id="peProdSalesChart" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.productionSales) }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        {}
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">HDPE Capacity · Production · Sales&nbsp;(MM&nbsp;lbs)</h4>
          <div className="h-[380px]">
            <canvas id="hdpeProdSalesChart" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.hdpe) }}
            />
          </div>
        </div>

        {}
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">LDPE Capacity · Production · Sales&nbsp;(MM&nbsp;lbs)</h4>
          <div className="h-[380px]">
            <canvas id="ldpeProdSalesChart" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.ldpe) }}
            />
          </div>
        </div>

        {}
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">LLDPE Capacity · Production · Sales&nbsp;(MM&nbsp;lbs)</h4>
          <div className="h-[380px]">
            <canvas id="lldpeProdSalesChart" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.lldpe) }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SnapshotPE;
